import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface Rating {
  id: string;
  raterId: string;
  raterName: string;
  raterTeam: 'black' | 'white';
  targetId: string;
  targetName: string;
  targetTeam: 'black' | 'white';
  score: number;
  comment: string;
  createdAt: number;
}

function dbRowToRating(row: Record<string, unknown>): Rating {
  return {
    id: row.id as string,
    raterId: row.rater_id as string,
    raterName: row.rater_name as string,
    raterTeam: row.rater_team as 'black' | 'white',
    targetId: row.target_id as string,
    targetName: row.target_name as string,
    targetTeam: row.target_team as 'black' | 'white',
    score: row.score as number,
    comment: (row.comment as string) ?? '',
    createdAt: row.created_at as number,
  };
}

export function useRatings() {
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRatings = useCallback(async () => {
    const { data } = await supabase
      .from('ratings')
      .select('*')
      .order('created_at', { ascending: false });
    if (data) setRatings(data.map(r => dbRowToRating(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRatings();

    const channel = supabase
      .channel(`ratings-realtime-${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ratings' }, (payload) => {
        const { eventType, new: newRow, old: oldRow } = payload;
        if (eventType === 'INSERT') {
          const r = dbRowToRating(newRow as Record<string, unknown>);
          setRatings(prev => [r, ...prev]);
        } else if (eventType === 'UPDATE') {
          const r = dbRowToRating(newRow as Record<string, unknown>);
          setRatings(prev => prev.map(x => x.id === r.id ? r : x));
        } else if (eventType === 'DELETE') {
          const deletedId = (oldRow as Record<string, unknown>).id as string;
          setRatings(prev => prev.filter(x => x.id !== deletedId));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchRatings]);

  const submitRatings = useCallback(async (
    raterId: string,
    raterName: string,
    raterTeam: 'black' | 'white',
    items: { targetId: string; targetName: string; targetTeam: 'black' | 'white'; score: number; comment: string }[],
  ) => {
    const now = Date.now();
    const rows = items.map((item, i) => ({
      id: `${raterId}-${item.targetId}-${now + i}`,
      rater_id: raterId,
      rater_name: raterName,
      rater_team: raterTeam,
      target_id: item.targetId,
      target_name: item.targetName,
      target_team: item.targetTeam,
      score: item.score,
      comment: item.comment,
      created_at: now + i,
    }));

    // Upsert by rater + target combo (overwrite previous rating)
    for (const row of rows) {
      // Delete existing rating for this rater→target pair
      await supabase.from('ratings').delete()
        .eq('rater_id', row.rater_id)
        .eq('target_id', row.target_id);
    }
    await supabase.from('ratings').insert(rows);
  }, []);

  const clearAllRatings = useCallback(async () => {
    setRatings([]);
    await supabase.from('ratings').delete().not('id', 'is', null);
  }, []);

  // Ratings I've submitted (by raterId)
  const getMyRatings = useCallback((raterId: string): Map<string, Rating> => {
    const map = new Map<string, Rating>();
    ratings.filter(r => r.raterId === raterId).forEach(r => map.set(r.targetId, r));
    return map;
  }, [ratings]);

  return { ratings, loading, submitRatings, clearAllRatings, getMyRatings };
}
